﻿import { useState, useCallback, useRef } from 'react'
import { SSE_EVENTS } from '../constants'
import { TYPEWRITER_CONFIG } from '../config'
import * as api from '../api'
import { handleApiError } from '../utils/errorHandler'
import type { SkillKey, Message } from '../types'

interface UseChatOptions {
  activeSkill: SkillKey
  activeHistory: string | null
  userId: string
  onMessageComplete?: (conversationId: string, firstUserMessage: string) => void
  onSuggestedQuestionsLoad?: (questions: string[]) => void
}

interface UseChatReturn {
  messages: Message[]
  isLoading: boolean
  sendMessage: (content: string) => Promise<void>
  stopResponse: () => void
  clearMessages: () => void
  setMessages: (messages: Message[]) => void
  handleLike: (msgId: string) => Promise<void>
  handleDislike: (msgId: string) => Promise<void>
  updateMessage: (msgId: string, updates: Partial<Message>) => void
}

/**
 * 聊天相关逻辑的自定义 Hook
 */
export const useChat = ({
  activeSkill,
  activeHistory,
  userId,
  onMessageComplete,
  onSuggestedQuestionsLoad
}: UseChatOptions): UseChatReturn => {
  const [messages, setMessages] = useState<Message[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const abortControllerRef = useRef<AbortController | null>(null)

  // 流式输出相关
  const totalContentRef = useRef('') // 从 API 累积的总内容
  const displayedLengthRef = useRef(0) // 已显示的长度
  const currentAssistantMsgIdRef = useRef('')
  const shouldStopRef = useRef(false)
  const messageIdUpdatedRef = useRef(false)
  const typewriterTimerRef = useRef<number | null>(null)
  const sseMessageCountRef = useRef(0) // SSE 消息计数器（用于日志节流）
  const charAccumulatorRef = useRef(0) // 字符累积器（浮点数，用于平滑输出）

  /**
   * 打字机效果：定期更新显示内容
   * 速度：每秒20个字符，链接和图片内容立即显示
   */
  const startTypewriter = useCallback(() => {
    if (typewriterTimerRef.current) return // 已在运行

    console.log('[Queue] Start processing')
    const { tickInterval, charsPerTick } = TYPEWRITER_CONFIG

    /**
     * 查找链接/图片结束位置，用于跳过链接内容
     * 图片：跳过整个段落（直到下一个空行或内容结束）
     * 裸 URL：跳过整个 URL
     */
    const findLinkEnd = (content: string, startPos: number): number => {
      // 检测 HTML 链接 <a ...>...</a>
      if (content.slice(startPos, startPos + 2) === '<a') {
        const closeTag = content.indexOf('</a>', startPos)
        if (closeTag !== -1) {
          return closeTag + 4 // 包含 </a>
        }
      }

      // 检测 Markdown 图片 ![alt](url)
      if (content.slice(startPos, startPos + 2) === '![') {
        const imgMatch = content.slice(startPos).match(/^!\[([^\]]*)\]\(([^)]+)\)/)
        if (imgMatch) {
          const imgEnd = startPos + imgMatch[0].length
          // 跳过图片后面的文字直到下一个空行（双换行）或内容结束
          const afterImg = content.slice(imgEnd)
          const nextParaIndex = afterImg.indexOf('\n\n')
          if (nextParaIndex !== -1) {
            return imgEnd + nextParaIndex
          }
          // 没有下一个段落，跳到内容结束
          return content.length
        }
      }

      // 检测 Markdown 链接 [text](url)
      if (content[startPos] === '[') {
        const linkMatch = content.slice(startPos).match(/^\[([^\]]*)\]\(([^)]+)\)/)
        if (linkMatch) {
          return startPos + linkMatch[0].length
        }
      }

      // 检测裸 URL（https:// 或 http:// 开头）
      // URL 允许字符：字母、数字、.-_/?:=&%#~+
      // 遇到中文标点、空格、换行等结束
      if (content.slice(startPos, startPos + 8) === 'https://' ||
          content.slice(startPos, startPos + 7) === 'http://') {
        const urlMatch = content.slice(startPos).match(/^(https?:\/\/[a-zA-Z0-9.\-_/?:=&%#~+]+)/)
        if (urlMatch) {
          console.log('[Queue] Skip bare URL:', urlMatch[0])
          return startPos + urlMatch[0].length
        }
      }

      return -1 // 不是链接/图片
    }

    const tick = () => {
      if (shouldStopRef.current) {
        typewriterTimerRef.current = null
        console.log('[Queue] Processing stopped by user')
        return
      }

      const total = totalContentRef.current.length
      let displayed = displayedLengthRef.current

      if (displayed < total) {
        // 累积字符（浮点数）
        charAccumulatorRef.current += charsPerTick

        // 当累积 >= 1 时，显示一个字符
        if (charAccumulatorRef.current >= 1) {
          const charsToShow = Math.floor(charAccumulatorRef.current)
          charAccumulatorRef.current -= charsToShow

          displayed = Math.min(displayed + charsToShow, total)

          // 检测当前位置是否有链接/图片，有则直接跳到结束
          const linkEnd = findLinkEnd(totalContentRef.current, displayed - 1)
          if (linkEnd > displayed) {
            displayed = Math.min(linkEnd, total)
            console.log('[Queue] Skip link/image, jump to position:', displayed)
          }

          displayedLengthRef.current = displayed

          // 渲染当前显示的内容（原始 Markdown，由 MarkdownRenderer 解析）
          const contentToShow = totalContentRef.current.slice(0, displayedLengthRef.current)

          setMessages(prev => {
            const targetMsg = prev.find(m => m.id === currentAssistantMsgIdRef.current)
            if (!targetMsg) {
              return prev
            }
            return prev.map(msg =>
              msg.id === currentAssistantMsgIdRef.current
                ? { ...msg, content: contentToShow }
                : msg
            )
          })
        }

        typewriterTimerRef.current = window.setTimeout(tick, tickInterval)
      } else {
        // 已追上，等待新内容（暂停定时器，由新数据触发重启）
        typewriterTimerRef.current = null
        charAccumulatorRef.current = 0 // 重置累积器
        console.log('[Queue] Processing done (waiting for new data)')
      }
    }

    typewriterTimerRef.current = window.setTimeout(tick, tickInterval)
  }, [])

  /**
   * 唤醒打字机（有新数据时调用）
   */
  const wakeTypewriter = useCallback(() => {
    if (!typewriterTimerRef.current && !shouldStopRef.current) {
      startTypewriter()
    }
  }, [startTypewriter])

  /**
   * 停止打字机
   */
  const stopTypewriter = useCallback(() => {
    if (typewriterTimerRef.current) {
      clearTimeout(typewriterTimerRef.current)
      typewriterTimerRef.current = null
    }
  }, [])

  /**
   * 更新指定消息
   */
  const updateMessage = useCallback((msgId: string, updates: Partial<Message>) => {
    setMessages(prev => prev.map(msg =>
      msg.id === msgId ? { ...msg, ...updates } : msg
    ))
  }, [])

  /**
   * 清除所有消息
   */
  const clearMessages = useCallback(() => {
    setMessages([])
  }, [])

  /**
   * 设置消息列表（用于加载历史记录）
   */
  const setMessagesList = useCallback((newMessages: Message[]) => {
    setMessages(newMessages)
  }, [])

  /**
   * 停止响应
   */
  const stopResponse = useCallback(() => {
    shouldStopRef.current = true
    stopTypewriter()
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    // 立即显示全部剩余内容（原始 Markdown）
    const content = totalContentRef.current
    setMessages(prev => prev.map(msg => {
      if (msg.id !== currentAssistantMsgIdRef.current) return msg
      return { ...msg, content, loading: false, stopped: true }
    }))
    setIsLoading(false)
  }, [stopTypewriter])

  /**
   * 加载建议问题
   */
  const loadSuggestedQuestions = useCallback(async (messageId: string) => {
    try {
      const questions = await api.getSuggestedQuestions(activeSkill, messageId, userId)
      if (onSuggestedQuestionsLoad) {
        onSuggestedQuestionsLoad(questions)
      }
    } catch (error) {
      console.error('Failed to load suggested questions:', error)
    }
  }, [activeSkill, userId, onSuggestedQuestionsLoad])

  /**
   * 发送消息
   */
  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim()) return

    const userContent = content.trim()
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: userContent,
    }

    const assistantId = `assistant-${Date.now()}`
    const loadingMessage: Message = {
      id: assistantId,
      role: 'assistant',
      content: '',
      loading: true,
    }

    setMessages(prev => [...prev, userMessage, loadingMessage])
    setIsLoading(true)

    // 重置状态
    shouldStopRef.current = false
    totalContentRef.current = ''
    displayedLengthRef.current = 0
    currentAssistantMsgIdRef.current = assistantId
    messageIdUpdatedRef.current = false
    sseMessageCountRef.current = 0 // 重置 SSE 消息计数器
    charAccumulatorRef.current = 0 // 重置字符累积器

    abortControllerRef.current = new AbortController()

    // 启动打字机
    startTypewriter()

    try {
      const response = await api.sendMessage(activeSkill, userContent, activeHistory, userId, abortControllerRef.current.signal)

      if (!response.body) {
        throw new Error('Response body is null')
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let isComplete = false

      const processStream = async () => {
        try {
          while (!isComplete && !shouldStopRef.current) {
            const { done, value } = await reader.read()

            if (done) {
              isComplete = true
              break
            }

            buffer += decoder.decode(value, { stream: true })
            const lines = buffer.split('\n')
            buffer = lines.pop() || ''

            for (const line of lines) {
              const trimmedLine = line.trim()
              if (!trimmedLine || !trimmedLine.startsWith('data:')) continue

              const jsonStr = trimmedLine.slice(5).trim()
              if (!jsonStr) continue

              try {
                const data = JSON.parse(jsonStr)

                if (data.event === SSE_EVENTS.MESSAGE_END) {
                  console.log('[SSE Event] message_end - Stream completed', {
                    totalMessages: sseMessageCountRef.current,
                    totalLength: totalContentRef.current.length
                  })
                  isComplete = true
                  return
                }

                if (data.event === SSE_EVENTS.MESSAGE) {
                  // 只更新一次消息 ID
                  if (data.id && data.conversation_id && !messageIdUpdatedRef.current) {
                    messageIdUpdatedRef.current = true
                    currentAssistantMsgIdRef.current = data.id
                    setMessages(prev => prev.map(msg =>
                      msg.id === assistantId
                        ? { ...msg, id: data.id, conversationId: data.conversation_id }
                        : msg
                    ))

                    if (onMessageComplete) {
                      onMessageComplete(data.conversation_id, userContent)
                   }
                  }

                  // 累积内容并唤醒打字机
                  if (data.answer) {
                    sseMessageCountRef.current++
                    const count = sseMessageCountRef.current
                    // 首次、每5条、打印日志
                    if (count === 1 || count % 5 === 0) {
                      console.log('[SSE Event] message', {
                        count,
                        chunkLength: data.answer.length,
                        totalLength: (totalContentRef.current + data.answer).length
                      })
                    }
                    totalContentRef.current += data.answer
                    wakeTypewriter()
                  }
                }

                if (data.event === SSE_EVENTS.WORKFLOW_FINISHED) {
                  isComplete = true
                  if (data.message_id && onSuggestedQuestionsLoad) {
                    loadSuggestedQuestions(data.message_id)
                  }
                  return
                }
              } catch {
                // 非 JSON 数据块，忽略
              }
            }
          }

          // 处理缓冲区剩余数据
          if (buffer.trim().startsWith('data:')) {
            try {
              const data = JSON.parse(buffer.trim().slice(5))
              if (data.event === SSE_EVENTS.MESSAGE && data.answer) {
                totalContentRef.current += data.answer
                wakeTypewriter()
              }
            } catch {
              // 忽略解析错误
            }
          }
        } catch (streamError) {
          if (streamError instanceof Error && streamError.name === 'AbortError') {
            // 已停止
          } else {
            throw streamError
          }
        } finally {
          reader.releaseLock()
        }
      }

      await processStream()

      // 等待打字机追上
      const waitForTypewriter = (): Promise<void> => {
        return new Promise(resolve => {
          const check = () => {
            if (displayedLengthRef.current >= totalContentRef.current.length) {
              resolve()
            } else {
              setTimeout(check, 50)
            }
          }
          check()
        })
      }

      await waitForTypewriter()
      stopTypewriter()
      console.log('[Queue] Processing done - All content rendered')

      // 最终渲染（确保 loading 状态更新）
      setMessages(prev => {
        const msg = prev.find(m => m.id === currentAssistantMsgIdRef.current)
        // 只有 loading 状态需要更新时才渲染
        if (msg?.loading) {
          return prev.map(m =>
            m.id === currentAssistantMsgIdRef.current
              ? { ...m, loading: false }
              : m
          )
        }
        return prev
      })

    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('[SSE] Stream aborted by user')
      } else {
        console.error('[SSE] Stream error:', error)
        handleApiError(error, '消息发送失败')
        setMessages(prev => prev.filter(msg => msg.id !== currentAssistantMsgIdRef.current))
      }
    } finally {
      stopTypewriter()
      setIsLoading(false)
      abortControllerRef.current = null
    }
  }, [activeSkill, activeHistory, userId, onMessageComplete, onSuggestedQuestionsLoad, startTypewriter, stopTypewriter, wakeTypewriter, loadSuggestedQuestions])

  /**
   * 处理反馈的公共方法
   */
  const handleFeedback = useCallback(async (msgId: string, type: 'like' | 'dislike') => {
    const msg = messages.find(m => m.id === msgId)
    if (!msg) return

    const isLiked = type === 'like'
    const wasActive = isLiked ? msg.liked : msg.disliked
    const updates = isLiked
      ? { liked: !wasActive, disliked: false }
      : { disliked: !wasActive, liked: false }

    updateMessage(msgId, updates)

    try {
      if (!wasActive) {
        await api.sendFeedback(activeSkill, msgId, type, userId)
      } else {
        await api.cancelFeedback(activeSkill, msgId, userId)
      }
    } catch (error) {
      handleApiError(error, '反馈操作失败')
      // 回滚 UI
      updateMessage(msgId, { liked: msg.liked, disliked: msg.disliked })
    }
  }, [messages, activeSkill, userId, updateMessage])

  /**
   * 点赞
   */
  const handleLike = useCallback(async (msgId: string) => {
    await handleFeedback(msgId, 'like')
  }, [handleFeedback])

  /**
   * 点踩
   */
  const handleDislike = useCallback(async (msgId: string) => {
    await handleFeedback(msgId, 'dislike')
  }, [handleFeedback])

  return {
    messages,
    isLoading,
    sendMessage,
    stopResponse,
    clearMessages,
    setMessages: setMessagesList,
    handleLike,
    handleDislike,
    updateMessage
  }
}
